import { HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, effect, inject, signal } from '@angular/core';
import { UserService } from '@cfp/core';
import { BuildingSearchFiltersService } from '@cfp/shared/building/filters';
import { PortfolioLayoutPageBase } from '@cfp/shared/portfolio/layout';
import { RoadmapPortfolioDetails, RoadmapService } from '@cfp/shared/roadmap';
import {
  RoadmapService as AssetRoadmapService,
  RoadmapPortfolioDetailsDTO,
  RoadmapPortfolioDetailsDisplayLabelsYearDTO
} from 'csr-asset-app';
import { CustomerStylingDTO } from 'csr-login-app';
import { from, mergeMap, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { NO_ROADMAP_LABELS } from '../data/no-roadmap-labels.data';
import { NO_ROADMAP_DATA } from '../data/no-roadmap.data';
import { mapRoadmapPortfolioDetailsToRoadmapBuildingDetails } from '../mappers';
import { PortfolioReportSummary } from '../portfolio-report-summary';

@Component({
  selector: 'cfp-portfolios-report-print-page',
  templateUrl: 'portfolio-report-print.page.html',
  imports: [PortfolioReportSummary],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioReportPrintPage extends PortfolioLayoutPageBase implements OnInit {
  protected readonly details = signal<RoadmapPortfolioDetails>(NO_ROADMAP_DATA);
  protected readonly hasRoadmap = signal<boolean>(false);
  protected readonly hasRoadmapLicence = signal<boolean>(false);
  protected readonly isLoading = signal<boolean>(false);
  protected readonly roadmapPortfolioDetails = signal<RoadmapPortfolioDetailsDTO | undefined>(undefined);
  protected readonly totals = signal<{ [key: string]: RoadmapPortfolioDetailsDisplayLabelsYearDTO }>(NO_ROADMAP_LABELS);
  protected readonly customLogo = signal<string | undefined>(undefined);

  private readonly roadmapService = inject(RoadmapService);
  private readonly userService = inject(UserService);
  private readonly assetRoadmapService = inject(AssetRoadmapService);
  private readonly buildingSearchFiltersService = inject(BuildingSearchFiltersService);

  constructor() {
    super();

    effect(() => {
      const portfolioId = this.portfolioId();
      const isLoadingSearchFilters = this.buildingSearchFiltersService.isLoadingSearchFilters();
      if (!portfolioId || isLoadingSearchFilters) {
        return;
      }

      const searchFilters = this.buildingSearchFiltersService.searchFilters();
      this.assetRoadmapService
        .v2PortfoliosIdRoadmapYearsPost(searchFilters, portfolioId)
        .subscribe((roadmapPortfolioDetailsDTO: RoadmapPortfolioDetailsDTO) => {
          this.roadmapPortfolioDetails.set(roadmapPortfolioDetailsDTO);

          this.hasRoadmap.set(false);
          if (roadmapPortfolioDetailsDTO?.buildings?.length > 0) {
            this.hasRoadmap.set(true);
            this.details.set(
              mapRoadmapPortfolioDetailsToRoadmapBuildingDetails(
                roadmapPortfolioDetailsDTO,
                this.roadmapService.getStartYear(
                  roadmapPortfolioDetailsDTO.buildings.map((building) => building.years.map((year) => year.year)).flat()
                )
              )
            );
            this.totals.set(roadmapPortfolioDetailsDTO.displayLabels || NO_ROADMAP_LABELS);
          }
        });
    });
  }

  public ngOnInit(): void {
    this.isLoading.set(true);

    this.userService
      .hasLicense('roadmap')
      .pipe(
        map((hasLicense: boolean) => {
          this.hasRoadmapLicence.set(hasLicense);
          return hasLicense;
        }),
        mergeMap(() =>
          this.userService.getCustomerStyling().pipe(
            mergeMap((customerStyling?: CustomerStylingDTO) => {
              this.customLogo.set(undefined);
              if (customerStyling?.logoType) {
                return this.userService.getUserCustomerStylingImage().pipe(
                  mergeMap((response: HttpResponse<Blob>) => {
                    if (!response.body) {
                      return of(null);
                    }
                    return from(this.convertBlobToBase64(response.body)).pipe(
                      tap((base64Image: string) => {
                        this.customLogo.set(base64Image);
                      })
                    );
                  })
                );
              } else {
                return of(null);
              }
            })
          )
        )
      )
      .subscribe(() => {
        this.isLoading.set(false);
      });
  }

  private convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
